import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

export interface Lead {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  message: string;
  source: string;
  property_id?: string | null;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost' | 'archived' | 'perdido';
  assigned_to?: string | null;
  converted_to_client_id?: string | null;
  metadata?: any;
  created_at: string;
  updated_at: string;
  property?: {
    title: string;
    listing_id: string;
    address: string;
    images?: any[];
    list_price?: number;
    bedrooms?: number;
    bathrooms?: number;
  } | null;
  assigned_user?: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string | null;
  } | null;
}

export const useLeads = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchLeads = async () => {
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        setLoading(true);
        const { data: leadsData, error } = await supabase
          .from('leads_all')
          .select(`
            id,
            name,
            email,
            phone,
            message,
            source,
            status,
            assigned_to,
            property_listing_id,
            metadata,
            created_at,
            updated_at,
            origin,
            portal
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const convertedLeads = (leadsData || []).map(lead => {
          const portalStr = (lead.portal as string | null) || '';
          const src = portalStr
            ? (portalStr.toLowerCase().includes('olx') ? 'olx' : 'portal')
            : ((lead.source || 'website').toLowerCase() === 'site' ? 'website' : (lead.source || 'website'));
          return {
            id: lead.id,
            name: lead.name || 'Nome não informado',
            email: lead.email,
            phone: lead.phone,
            message: lead.message || '',
            source: src,
            property_id: lead.property_listing_id,
            status: lead.status as Lead['status'],
            assigned_to: lead.assigned_to,
            converted_to_client_id: null,
            metadata: lead.metadata || {},
            created_at: lead.created_at,
            updated_at: lead.updated_at,
            property: null,
            assigned_user: null
          } as Lead;
        });

        const dedupMap = new Map<string, Lead>();
        for (const l of convertedLeads) {
          const emailKey = (l.email || '').toLowerCase();
          const phoneKey = (l.phone || '').replace(/\D/g, '').slice(-11);
          const key = `${emailKey}|${phoneKey}|${l.property_id || ''}|${l.source}`;
          if (!dedupMap.has(key)) dedupMap.set(key, l);
        }
        const dedupedLeads = Array.from(dedupMap.values());

        setLeads(dedupedLeads);
        return; // sucesso
      } catch (error: any) {
        console.error(`Erro ao carregar leads (tentativa ${attempt}):`, error);
        if (String(error?.message || '').includes('Failed to fetch') && attempt < maxAttempts) {
          const delay = 500 * attempt;
          await new Promise(res => setTimeout(res, delay));
          continue;
        }
        toast({ title: 'Erro', description: 'Não foi possível carregar os leads', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    }
  };

  const updateLeadStatus = async (leadId: string, status: Lead['status']) => {
    try {
      // Atualizar diretamente em leads_all e deixar triggers sincrarem
      const { error } = await supabase
        .from('leads_all')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, status } : lead
      ));

      toast({
        title: "Status atualizado",
        description: "Status do lead foi atualizado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status",
        variant: "destructive",
      });
    }
  };

  const assignLead = async (leadId: string, userId: string) => {
    try {
      // Atualizar diretamente em leads_all 
      const { error } = await supabase
        .from('leads_all')
        .update({ assigned_to: userId, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      await fetchLeads(); // Recarregar para pegar dados do usuário atribuído

      toast({
        title: "Lead atribuído",
        description: "Lead foi atribuído com sucesso",
      });
    } catch (error) {
      console.error('Erro ao atribuir lead:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atribuir o lead",
        variant: "destructive",
      });
    }
  };

  const getLeadById = async (leadId: string): Promise<Lead | null> => {
    try {
      // Buscar em leads_all como fonte única
      const { data: leadData } = await supabase
        .from('leads_all')
        .select(`
          id,
          name,
          email,
          phone,
          message,
          source,
          status,
          assigned_to,
          property_listing_id,
          metadata,
          created_at,
          updated_at,
          origin,
          portal
        `)
        .eq('id', leadId)
        .maybeSingle();

      if (leadData) {
        const portalStr = (leadData.portal as string | null) || '';
        const src = portalStr
          ? (portalStr.toLowerCase().includes('olx') ? 'olx' : 'portal')
          : ((leadData.source || 'website').toLowerCase() === 'site' ? 'website' : (leadData.source || 'website'));
        return {
          id: leadData.id,
          name: leadData.name || 'Nome não informado',
          email: leadData.email,
          phone: leadData.phone,
          message: leadData.message || '',
          source: src,
          property_id: leadData.property_listing_id,
          status: leadData.status as Lead['status'],
          assigned_to: leadData.assigned_to,
          converted_to_client_id: null,
          metadata: leadData.metadata || {},
          created_at: leadData.created_at,
          updated_at: leadData.updated_at,
          property: null,
          assigned_user: null
        };
      }

      return null;
    } catch (error) {
      console.error('Erro ao carregar lead:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchLeads();

    // Escutar mudanças apenas na tabela leads_all com realtime
    const channel = supabase
      .channel('leads_all_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'leads_all' },
        () => fetchLeads()
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'leads_all' },
        () => fetchLeads()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const deleteLead = async (leadId: string) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      
      // Para leads de portal, marcar como perdido
      const srcLower = (lead?.source || '').toLowerCase();
      if (srcLower === 'portal' || srcLower === 'olx') {
        await updateLeadStatus(leadId, 'lost');
        toast({
          title: "Lead marcado como perdido",
          description: "Leads de portais não podem ser excluídos, apenas marcados como perdidos",
        });
        return;
      }

      // Para leads do site, usar leads_all para deletar
      const { error } = await supabase
        .from('leads_all')
        .delete()
        .eq('id', leadId);

      if (error) throw error;

      // Remover da lista local
      setLeads(prev => prev.filter(lead => lead.id !== leadId));

      toast({
        title: "Lead excluído",
        description: "Lead foi excluído com sucesso",
      });
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o lead",
        variant: "destructive",
      });
    }
  };

  const convertLeadToClient = async (leadId: string) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) throw new Error('Lead não encontrado');

      let clientSource = lead.source;
      
      // Detectar origem de portal a partir de lead.portal, metadata.portal ou string de source
      const rawPortal = (lead as any).portal || lead.metadata?.portal || '';
      const portalName = typeof rawPortal === 'string' ? rawPortal.toLowerCase() : '';
      if (portalName) {
        if (portalName.includes('olx')) {
          clientSource = 'OLX';
        } else if (portalName.includes('vivareal') || portalName.includes('viva real')) {
          clientSource = 'Viva Real';
        } else if (portalName.includes('zap')) {
          clientSource = 'ZAP Imóveis';
        } else {
          // Manter o nome original do portal se não conseguir identificar
          clientSource = rawPortal as string;
        }
      } else if (lead.source?.toLowerCase().includes('portal') && lead.source?.toLowerCase().includes('olx')) {
        clientSource = 'OLX';
      }

      // Marcar lead como convertido
      await updateLeadStatus(leadId, 'converted');

      // Inserir cliente na tabela clients preservando a origem
      try {
        let sourceForDB = (lead.source || 'manual').toLowerCase();

        const rawPortalDB = (lead as any).portal || lead.metadata?.portal || '';
        const portalNameDB = typeof rawPortalDB === 'string' ? rawPortalDB.toLowerCase() : '';
        if (portalNameDB) {
          if (portalNameDB.includes('olx')) {
            sourceForDB = 'olx';
          } else if (portalNameDB.includes('whatsapp')) {
            sourceForDB = 'whatsapp';
          } else if (portalNameDB.includes('facebook')) {
            sourceForDB = 'facebook';
          } else if (portalNameDB.includes('instagram')) {
            sourceForDB = 'instagram';
          } else {
            // Portais diversos: classificar como website e manter portal em metadata
            sourceForDB = 'website';
          }
        } else if (lead.source?.toLowerCase().includes('portal')) {
          // Heurística com base no texto do source
          if (lead.source.toLowerCase().includes('olx')) sourceForDB = 'olx';
          else sourceForDB = 'website';
        }

        const { error: clientInsertError } = await supabase
          .from('clients')
          .insert({
            assigned_to: lead.assigned_to || null,
            name: lead.name,
            email: lead.email || null,
            phone: lead.phone || null,
            source: sourceForDB, // Usar source normalizado (olx, website, etc)
            status: 'client',
            interests: [],
            budget: { min: 0, max: 0 }, // TODO: Avaliar remoção desta coluna
            location: '',
            notes: `Convertido do lead em ${new Date().toLocaleDateString('pt-BR')}. ${lead.message || ''}`.trim(),
            last_contact: new Date().toISOString(),
            metadata: {
              original_portal: ((lead as any).portal || lead.metadata?.portal || null),
              portal_client_listing_id: lead.metadata?.client_listing_id || lead.property_id || null,
              original_source: lead.source,
            },
            converted_from_lead_id: lead.metadata?.portal_lead_id ? null : lead.id,
            converted_from_portal_lead_id: lead.metadata?.portal_lead_id || null,
          });

        if (clientInsertError) {
          console.error('Erro ao inserir cliente:', clientInsertError);
        }
      } catch (e) {
        console.error('Falha ao criar cliente na tabela clients:', e);
      }

      // Se for um lead de portal, podemos atualizar a fonte na metadata para referência
      if (lead.metadata?.portal_lead_id) {
        const { error: metadataError } = await supabase
          .from('leads_portais')
          .update({ 
            status: 'convertido',
            updated_at: new Date().toISOString()
          })
          .eq('id', lead.metadata.portal_lead_id);

        if (metadataError) {
          console.error('Erro ao atualizar metadata do lead portal:', metadataError);
        }
      }

      toast({
        title: "Lead convertido",
        description: `Lead foi convertido para cliente com sucesso. Origem: ${clientSource}`,
      });

      return true;
    } catch (error) {
      console.error('Erro ao converter lead:', error);
      toast({
        title: "Erro",
        description: "Não foi possível converter o lead",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    leads,
    loading,
    fetchLeads,
    updateLeadStatus,
    assignLead,
    getLeadById,
    deleteLead,
    convertLeadToClient
  };
};